export const ROLES = ['tenant', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const USER_STATUSES = ['pending', 'active', 'deactivated'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const UNIT_STATUSES = ['occupied', 'vacant'] as const;
export type UnitStatus = (typeof UNIT_STATUSES)[number];

export const TICKET_STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  deactivated: 'Deactivated',
};

export const UNIT_STATUS_LABELS: Record<UnitStatus, string> = {
  occupied: 'Occupied',
  vacant: 'Vacant',
};

/** Order matters: this is the dropdown order, with "Other" last. */
export const TICKET_CATEGORIES = [
  { id: 'plumbing', label: 'Plumbing / Leakage' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'water_supply', label: 'Water Supply' },
  { id: 'lift', label: 'Lift / Elevator' },
  { id: 'door_lock', label: 'Door / Lock' },
  { id: 'seepage', label: 'Seepage / Dampness' },
  { id: 'painting', label: 'Painting / Walls' },
  { id: 'appliance', label: 'Appliance' },
  { id: 'pest_control', label: 'Pest Control' },
  { id: 'housekeeping', label: 'Housekeeping / Cleaning' },
  { id: 'parking', label: 'Parking' },
  { id: 'security', label: 'Security' },
  { id: 'internet', label: 'Internet / Cable' },
  { id: 'noise', label: 'Noise Complaint' },
  { id: 'other', label: 'Other' },
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number]['id'];
export const TICKET_CATEGORY_IDS = TICKET_CATEGORIES.map((c) => c.id) as [
  TicketCategory,
  ...TicketCategory[],
];

export function categoryLabel(id: string): string {
  return TICKET_CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

export const MAX_TICKET_IMAGES = 5;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_IMAGE_DIMENSION = 1920;

export const MESSAGE_MAX_LENGTH = 2000;
export const MAX_CHAT_IMAGES = 5;

/** Storage folder for a thread's chat photos (mirrored in storage.rules / firestore.rules). */
export const chatImageFolder = (thread: { ticketId: string } | { tenantUid: string }) =>
  'ticketId' in thread ? `tickets/${thread.ticketId}/chat` : `generalChats/${thread.tenantUid}`;

/** Inbox preview for a message: its text, or a calm photo summary when it has none. */
export function messagePreview(text: string, imageCount = 0): string {
  const trimmed = text.trim();
  if (trimmed) return trimmed.slice(0, 140);
  if (imageCount > 1) return `Sent ${imageCount} photos`;
  return imageCount === 1 ? 'Sent a photo' : '';
}
export const DESCRIPTION_MIN_LENGTH = 10;
export const DESCRIPTION_MAX_LENGTH = 2000;
export const DEFAULT_COUNTRY_CODE = '+1';
export const FUNCTIONS_REGION = 'us-central1';
export const MIN_FLOOR = 0;
export const MAX_FLOOR = 200;
